import {
    Component,
    input,
    signal,
    effect,
    viewChild,
    forwardRef,
    Type,
    Signal,
    InputSignal,
    WritableSignal,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { Extension } from "@codemirror/state";
import { CodeEditor } from "@acrodata/code-editor";
import { LanguageDescription } from "@codemirror/language";

import { OnChangeFn, OnTouchedFn } from "../../models";
import { CodeEditorThemeService } from "../../services/code-editor-theme.service";
import { CssEditorLanguageService } from "../services/css-editor-language.service";


@Component({
    selector: "css-editor",
    templateUrl: "./css-editor.component.html",
    styleUrls: ["./css-editor.component.scss"],
    providers: [{
        useExisting: forwardRef((): Type<CssEditorComponent> => CssEditorComponent),
        provide: NG_VALUE_ACCESSOR,
        multi: true
    }]
})
export class CssEditorComponent implements ControlValueAccessor {
    // View child statements.
    public editor: Signal<CodeEditor> = viewChild(CodeEditor);

    // Input properties.
    public extensions: InputSignal<Extension[]> = input([]);

    // Writable signals.
    private onTouched: WritableSignal<OnTouchedFn> = signal(null);
    private onChange: WritableSignal<OnChangeFn> = signal(null);

    protected language: WritableSignal<LanguageDescription> = signal(null);
    protected disabled: WritableSignal<boolean> = signal(false);
    protected theme: WritableSignal<Extension> = signal(null);
    protected value: WritableSignal<string> = signal("");


    constructor(private languageService: CssEditorLanguageService,
                private themeService: CodeEditorThemeService) {
        this.configureEditor();
    }

    private configureEditor(): void {
        // Set the editor theme (one-dark theme with minor overrides).
        this.theme.set(this.themeService.getThemeExtension());

        // Set the editor language.
        this.language.set(this.languageService.getLanguageDescription());

        // React to editor apparition or on-touched value change, to update the latter in the editor.
        effect((): void => {
            const editor: CodeEditor = this.editor();
            if (!editor) return;

            const onTouched: OnTouchedFn = this.onTouched();
            editor.registerOnTouched((): void => onTouched?.());
        });

        // React to editor apparition or on-change value change, to update the latter in the editor.
        effect((): void => {
            const editor: CodeEditor = this.editor();
            if (!editor) return;

            const onChange: OnChangeFn = this.onChange();
            editor.registerOnChange((value: string): void => onChange?.(value));
        });
    }

    public registerOnChange(fn: OnChangeFn): void {
        this.onChange.set(fn);
    }

    public registerOnTouched(fn: OnTouchedFn): void {
        this.onTouched.set(fn);
    }

    public writeValue(value: string): void {
        this.value.set(value);
    }

    public setDisabledState(disabled: boolean): void {
        this.disabled.set(disabled);
    }
}